import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Search, Star, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WORKOUT_TYPES, WORKOUT_CATEGORIES, getWorkoutType } from '../lib/workoutTypes';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Searchable workout-type picker with favorites pinned at the top.
 * @param {string} value - currently selected workout_type value
 * @param {(value: string) => void} onChange
 * @param {Array<{workout_type: string, use_count?: number}>} favorites
 */
export function WorkoutPicker({ value, onChange, favorites = [] }) {
    const { language } = useLanguage();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef(null);

    const selected = getWorkoutType(value);
    const SelectedIcon = selected.icon;

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Build favorite values (dedup, ordered by use_count desc)
    const favoriteValues = useMemo(() => {
        const seen = new Set();
        return favorites
            .slice()
            .sort((a, b) => (b.use_count || 0) - (a.use_count || 0))
            .map((f) => f.workout_type)
            .filter((v) => {
                if (seen.has(v)) return false;
                seen.add(v);
                return WORKOUT_TYPES.some((w) => w.value === v);
            })
            .slice(0, 6);
    }, [favorites]);

    // Filter by query (across label and category name)
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return WORKOUT_TYPES;
        return WORKOUT_TYPES.filter((w) => {
            const labelDe = w.label.de.toLowerCase();
            const labelEn = w.label.en.toLowerCase();
            const cat = WORKOUT_CATEGORIES[language][w.category]?.toLowerCase() || '';
            return labelDe.includes(q) || labelEn.includes(q) || cat.includes(q);
        });
    }, [query, language]);

    // Group filtered list by category for display
    const grouped = useMemo(() => {
        const groups = {};
        filtered.forEach((w) => {
            if (!groups[w.category]) groups[w.category] = [];
            groups[w.category].push(w);
        });
        return groups;
    }, [filtered]);

    const t = (de, en) => (language === 'de' ? de : en);

    const handleSelect = (v) => {
        onChange(v);
        setOpen(false);
        setQuery('');
    };

    return (
        <div ref={containerRef} className="relative">
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-muted border-2 border-border hover:border-primary/40 transition-colors"
                data-testid="workout-picker-trigger"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: `${selected.color}20` }}
                    >
                        <SelectedIcon className="w-4 h-4" style={{ color: selected.color }} />
                    </div>
                    <span className="font-medium text-sm truncate">{selected.label[language]}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute z-50 left-0 right-0 mt-1 bg-card border-2 border-border rounded-xl shadow-2xl overflow-hidden"
                        data-testid="workout-picker-dropdown"
                    >
                        {/* Search */}
                        <div className="p-2 border-b border-border bg-card sticky top-0">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                                <input
                                    type="text"
                                    autoFocus
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t('Sportart suchen…', 'Search sport…')}
                                    className="w-full pl-8 pr-8 py-2 text-sm rounded-lg bg-muted border border-border outline-none focus:border-primary"
                                    data-testid="workout-picker-search"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => setQuery('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* List */}
                        <div className="max-h-72 overflow-y-auto">
                            {/* Favorites */}
                            {!query && favoriteValues.length > 0 && (
                                <div className="px-2 pt-2">
                                    <div className="flex items-center gap-1.5 px-1.5 mb-1">
                                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                            {t('Favoriten', 'Favorites')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 mb-2">
                                        {favoriteValues.map((fv) => {
                                            const w = getWorkoutType(fv);
                                            const Icon = w.icon;
                                            const isActive = value === w.value;
                                            return (
                                                <button
                                                    key={`fav-${w.value}`}
                                                    type="button"
                                                    onClick={() => handleSelect(w.value)}
                                                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
                                                        isActive
                                                            ? 'bg-primary/15 text-primary ring-1 ring-primary/30'
                                                            : 'hover:bg-muted'
                                                    }`}
                                                    data-testid={`workout-picker-fav-${w.value}`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: w.color }} />
                                                    <span className="truncate">{w.label[language]}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Grouped categories */}
                            {Object.keys(grouped).length === 0 ? (
                                <p className="text-center text-xs text-muted-foreground py-6">
                                    {t('Keine Treffer', 'No matches')}
                                </p>
                            ) : (
                                Object.entries(grouped).map(([cat, items]) => (
                                    <div key={cat} className="px-2 pb-2">
                                        <div className="px-1.5 pt-2 pb-1">
                                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                                {WORKOUT_CATEGORIES[language][cat]}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1">
                                            {items.map((w) => {
                                                const Icon = w.icon;
                                                const isActive = value === w.value;
                                                return (
                                                    <button
                                                        key={w.value}
                                                        type="button"
                                                        onClick={() => handleSelect(w.value)}
                                                        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
                                                            isActive
                                                                ? 'bg-primary/15 text-primary ring-1 ring-primary/30'
                                                                : 'hover:bg-muted'
                                                        }`}
                                                        data-testid={`workout-picker-option-${w.value}`}
                                                    >
                                                        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: w.color }} />
                                                        <span className="truncate">{w.label[language]}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
